import React, { useState, useEffect } from "react";
import {
  Box,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
} from "@mui/material";
import { useMutation, useQuery } from "@apollo/client";
import { UPDATE_EMPLOYEE_DETAILS } from "../../../../../../../apis/organization-apis/people-apis/peopleApis";
import { GET_USER_DETAILS } from "../../../../../../../apis/dashboard-apis/dashboardDetailApi";
import ConfirmSnackbar from "../../../../../../../components/Snackbar/ConfirmSnackbar";
import ConfirmDialog from "../../../../../../../components/ConfirmDialog/ConfirmDialog";
import { MasterLookupItemsType, AccountCategoryKey } from "../../../../../../../utils/enums";
import {
  isSelfEmployee,
  SELF_OUT_OF_SCOPE_MESSAGE,
} from "../../../utils/selfScopeGuard";
const ComplianceScopeToggle = ({
  staffMember,
  originalEmployee,
  refetch,
  hasHrmsIntegration = false,
  optimisticInScope: externalOptimistic,
  onOptimisticScopeChange,
}) => {
  const [snackbarInfo, setSnackbarInfo] = useState(null);
  const [internalOptimistic, setInternalOptimistic] = useState(null);
  const [scopeNotes, setScopeNotes] = useState("");
  const [confirmOutOfScopeOpen, setConfirmOutOfScopeOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [updateEmployee] = useMutation(UPDATE_EMPLOYEE_DETAILS);
  const { data: userData } = useQuery(GET_USER_DETAILS);
  const currentUserName = userData?.getAccountDetails?.userDetails?.name || "";
  const currentEmployeeDataId =
    userData?.getAccountDetails?.userDetails?.employee_data_id;
  const isSelf =
    isSelfEmployee(originalEmployee, currentEmployeeDataId) ||
    isSelfEmployee(staffMember, currentEmployeeDataId);
  const getDefaultOutOfScopeReason = () =>
    `Marked out of scope by ${currentUserName?.trim() || "admin"}`;

  const applyOptimisticScope = (inScope, scopeReason) => {
    if (onOptimisticScopeChange) {
      onOptimisticScopeChange(inScope, scopeReason);
    } else {
      setInternalOptimistic(inScope);
      if (!inScope && scopeReason) {
        setScopeNotes(scopeReason);
      }
    }
  };

  const optimisticInScope = externalOptimistic ?? internalOptimistic;

  const serverInScope = staffMember?.in_scope === true;
  const isInScope = optimisticInScope ?? serverInScope;
  const hasTerminationDate = !!originalEmployee?.termination_date;
  const serverScopeReason =
    staffMember?.scopeReason || originalEmployee?.scope_reason || "";

  useEffect(() => {
    if (onOptimisticScopeChange === undefined) {
      setInternalOptimistic(null);
    }
  }, [serverInScope, onOptimisticScopeChange]);

  useEffect(() => {
    if (optimisticInScope !== null) return;

    if (isInScope) {
      setScopeNotes("");
    } else {
      setScopeNotes(serverScopeReason);
    }
  }, [isInScope, serverScopeReason, optimisticInScope]);

  const revertOptimisticScope = () => applyOptimisticScope(null);

  const getEmployeeEmail = () =>
    originalEmployee?.work_email ||
    originalEmployee?.emails?.find((e) => e.type === "work")?.email ||
    originalEmployee?.emails?.[0]?.email ||
    "";

  const buildOutOfScopeInput = (notes) => ({
    id: originalEmployee.id,
    email: getEmployeeEmail(),
    in_scope: false,
    scope_reason: notes.trim() || getDefaultOutOfScopeReason(),
    employee_type: MasterLookupItemsType.NOT_IN_SCOPE_USER_CATEGORY,
    employee_key: AccountCategoryKey.NON_PERSON,
  });

  // Marking someone out of scope revokes their login, so it always goes through
  // a confirmation. Marking back In Scope is safe and stays immediate.
  const handleToggle = (targetInScope) => {
    if (targetInScope === isInScope) return;
    if (targetInScope && hasTerminationDate) {
      setSnackbarInfo({
        message: "Clear the termination date before marking this employee as In Scope.",
        vertical: "top",
        horizontal: "right",
      });
      return;
    }

    if (!targetInScope) {
      if (isSelf) {
        setSnackbarInfo({
          message: SELF_OUT_OF_SCOPE_MESSAGE,
          vertical: "top",
          horizontal: "right",
        });
        return;
      }
      setConfirmOutOfScopeOpen(true);
      return;
    }

    return commitScopeChange(targetInScope);
  };

  const commitScopeChange = async (targetInScope) => {
    const email = getEmployeeEmail();
    if (!email) {
      setSnackbarInfo({ message: "Employee email not found", vertical: "top", horizontal: "right" });
      return;
    }

    const outOfScopeReason = targetInScope
      ? null
      : scopeNotes.trim() || getDefaultOutOfScopeReason();

    if (!targetInScope) {
      setScopeNotes(outOfScopeReason);
    }

    applyOptimisticScope(targetInScope, outOfScopeReason);

    try {
      const input = targetInScope
        ? {
            id: originalEmployee.id,
            email,
            in_scope: true,
            scope_reason: null,
            employee_type: MasterLookupItemsType.IN_SCOPE_USER_CATEGORY,
            employee_key: AccountCategoryKey.ACTIVE_USER,
          }
        : buildOutOfScopeInput(outOfScopeReason);

      const response = await updateEmployee({ variables: { input: [input] } });
      if (response?.data?.updateEmployeeDetails?.success) {
        setSnackbarInfo({
          message: `Employee marked as ${targetInScope ? "In Scope" : "Out of Scope"}`,
          vertical: "top",
          horizontal: "right",
        });
        if (refetch) refetch();
      } else {
        revertOptimisticScope();
        setSnackbarInfo({
          message: response?.data?.updateEmployeeDetails?.message || "Failed to update scope",
          vertical: "top",
          horizontal: "right",
        });
      }
    } catch (error) {
      revertOptimisticScope();
      setSnackbarInfo({ message: error?.message || "Failed to update scope", vertical: "top", horizontal: "right" });
    }
  };

  const handleConfirmOutOfScope = async () => {
    setConfirming(true);
    try {
      await commitScopeChange(false);
    } finally {
      setConfirming(false);
      setConfirmOutOfScopeOpen(false);
    }
  };

  const handleSaveNotes = async () => {
    if (isInScope) return;
    const trimmed = scopeNotes.trim();
    if (trimmed === serverScopeReason.trim()) return;

    const email = getEmployeeEmail();
    if (!email) return;

    try {
      const response = await updateEmployee({
        variables: { input: [buildOutOfScopeInput(scopeNotes)] },
      });
      if (response?.data?.updateEmployeeDetails?.success) {
        if (refetch) refetch();
      } else {
        setSnackbarInfo({
          message: response?.data?.updateEmployeeDetails?.message || "Failed to save notes",
          vertical: "top",
          horizontal: "right",
        });
      }
    } catch (error) {
      setSnackbarInfo({
        message: error?.message || "Failed to save notes",
        vertical: "top",
        horizontal: "right",
      });
    }
  };

  if (!staffMember) return null;

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", flexShrink: 0 }}>
        <ToggleButtonGroup
          value={isInScope ? "in_scope" : "out_of_scope"}
          exclusive
          size="small"
          onChange={(_, newValue) => {
            if (!newValue) return;
            handleToggle(newValue === "in_scope");
          }}
          sx={{
            borderRadius: "26px",
            border: "1px solid #E8EAED",
            gap: 0.2,
            overflow: "hidden",
            boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
            "& .MuiToggleButton-root": {
              border: "none",
              borderRadius: "36px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.75rem",
              px: 2,
              py: 0.75,
              minWidth: 88,
              color: "#6B7280",
              whiteSpace: "nowrap",
              transition: "all 0.2s ease-in-out",
              "&.Mui-selected": {
                backgroundColor: "#3B82F6",
                color: "#fff",
                boxShadow: "inset 0 1px 2px 0 rgba(0,0,0,0.05)",
                "&:hover": { backgroundColor: "#2563EB" },
              },
              "&:hover:not(.Mui-selected)": {
                backgroundColor: "#F9FAFB",
                color: "#374151",
              },
              "&.Mui-disabled": {
                opacity: 0.45,
              },
            },
            "& .MuiToggleButtonGroup-grouped": {
              border: "none",
            },
          }}
        >
          <Tooltip
            title={
              hasTerminationDate && !isInScope
                ? "Clear the termination date first to reinstate as In Scope"
                : isInScope
                ? "Currently In Scope"
                : "Mark as In Scope"
            }
            arrow
          >
            <span>
              <ToggleButton value="in_scope" disabled={hasTerminationDate && !isInScope}>
                In Scope
              </ToggleButton>
            </span>
          </Tooltip>
          <Tooltip
            title={
              isSelf && isInScope
                ? SELF_OUT_OF_SCOPE_MESSAGE
                : isInScope
                ? "Mark as Out of Scope"
                : "Currently Out of Scope"
            }
            arrow
          >
            <span>
              <ToggleButton value="out_of_scope" disabled={isSelf && isInScope}>
                Out of Scope
              </ToggleButton>
            </span>
          </Tooltip>
        </ToggleButtonGroup>

        {!isInScope && (
          <TextField
            size="small"
            multiline
            rows={2}
            placeholder="Additional notes (optional)"
            value={scopeNotes}
            onChange={(e) => setScopeNotes(e.target.value)}
            onBlur={handleSaveNotes}
            sx={{
              mt: 1,
              width: 260,
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                fontSize: "0.8125rem",
                lineHeight: 1.5,
                color: "#374151",
                backgroundColor: "#FAFBFC",
                transition: "border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease",
                "& fieldset": {
                  borderColor: "#E5E7EB",
                  borderWidth: "1px",
                },
                "&:hover fieldset": {
                  borderColor: "#D1D5DB",
                },
                "&.Mui-focused": {
                  backgroundColor: "#fff",
                  boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.12)",
                  "& fieldset": {
                    borderColor: "#93C5FD",
                    borderWidth: "1px",
                  },
                },
                "& textarea": {
                  color: "#374151",
                  "&::placeholder": {
                    color: "#9CA3AF",
                    opacity: 1,
                  },
                },
              },
            }}
          />
        )}
      </Box>
      <ConfirmDialog
        open={confirmOutOfScopeOpen}
        onClose={() => setConfirmOutOfScopeOpen(false)}
        onConfirm={handleConfirmOutOfScope}
        loading={confirming}
        heading="Mark out of scope?"
        buttonText="Mark Out of Scope"
        content={`${
          staffMember?.name || "This employee"
        } will lose access to Regodit and be excluded from compliance checks and employee workflows.`}
        note="You can reverse this by marking them In Scope again."
      />
      <ConfirmSnackbar snackbarInfo={snackbarInfo} />
    </>
  );
};

export default ComplianceScopeToggle;
